import { useLanguage } from '@/contexts/LanguageContext';
import SEOHead from '@/components/SEOHead';
import Layout from '@/components/v2/organisms/landing/layout/Layout';

const PrivacyPolicy = () => {
  const { language } = useLanguage();

  return (
    <Layout>
      <SEOHead 
        title={language === 'es' ? 'Políticas de Privacidad - Estancia Corta' : 'Privacy Policy - Estancia Corta'}
        description={language === 'es' ? 'Conoce nuestras políticas de privacidad y el manejo de datos personales en Estancia Corta.' : 'Learn about our privacy policies and personal data handling at Estancia Corta.'}
      />
      
      <div className="min-h-screen bg-gray-50 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg p-8 md:p-12">
            {language === 'es' ? (
              <>
                <h1 className="text-3xl font-bold text-gray-900 mb-8">Políticas de Privacidad</h1>
                
                <div className="prose prose-lg max-w-none text-gray-700">
                  <p className="text-lg mb-6">
                    En Estancia Corta, respetamos tu privacidad y nos comprometemos a proteger tus datos personales. 
                    Esta política de privacidad explica cómo recopilamos, utilizamos y protegemos tu información.
                  </p>

                  <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Información que Recopilamos</h2>
                  <p className="mb-4">Recopilamos los siguientes tipos de información personal:</p>
                  <ul className="list-disc pl-6 mb-6">
                    <li><strong>Nombre completo:</strong> Para identificarte durante el proceso de reserva y comunicación</li>
                    <li><strong>Correo electrónico:</strong> Para enviarte confirmaciones de reserva, actualizaciones y comunicaciones importantes</li>
                    <li><strong>Número de teléfono:</strong> Para contactarte directamente sobre tu reserva y brindarte soporte durante tu estadía</li>
                  </ul>

                  <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Cómo Utilizamos tu Información</h2>
                  <p className="mb-4">Utilizamos tus datos personales para:</p>
                  <ul className="list-disc pl-6 mb-6">
                    <li>Procesar y confirmar tus reservas</li>
                    <li>Comunicarnos contigo sobre tu estadía</li>
                    <li>Brindarte soporte y atención al cliente</li>
                    <li>Enviarte información relevante sobre nuestros servicios</li>
                    <li>Mejorar nuestros servicios y experiencia del usuario</li>
                  </ul>

                  <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Protección de Datos</h2>
                  <p className="mb-6">
                    Implementamos medidas de seguridad técnicas y organizativas apropiadas para proteger tus datos 
                    personales contra el acceso no autorizado, alteración, divulgación o destrucción.
                  </p>

                  <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Compartir Información</h2>
                  <p className="mb-6">
                    No vendemos, intercambiamos o transferimos tu información personal a terceros sin tu consentimiento, 
                    excepto cuando sea necesario para brindar nuestros servicios o cuando la ley lo requiera.
                  </p>

                  <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Tus Derechos</h2>
                  <p className="mb-4">Tienes derecho a:</p>
                  <ul className="list-disc pl-6 mb-6">
                    <li>Acceder a tus datos personales</li>
                    <li>Rectificar información incorrecta</li>
                    <li>Solicitar la eliminación de tus datos</li>
                    <li>Retirar tu consentimiento en cualquier momento</li>
                  </ul>

                  <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Contacto</h2>
                  <p className="mb-6">
                    Si tienes preguntas sobre esta política de privacidad o deseas ejercer tus derechos, 
                    puedes contactarnos en:
                  </p>
                  <ul className="list-none mb-6">
                    <li><strong>Email:</strong> pedro@estanciacorta.com</li>
                    <li><strong>Teléfono:</strong> +52 3340860642</li>
                  </ul>

                  <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Actualización de Políticas</h2>
                  <p className="mb-6">
                    Nos reservamos el derecho de actualizar esta política de privacidad en cualquier momento. 
                    Te notificaremos sobre cambios importantes a través del correo electrónico registrado.
                  </p>

                  <p className="text-sm text-gray-600 mt-8">
                    <strong>Última actualización:</strong> Enero 2025
                  </p>
                </div>
              </>
            ) : (
              <>
                <h1 className="text-3xl font-bold text-gray-900 mb-8">Privacy Policy</h1>
                
                <div className="prose prose-lg max-w-none text-gray-700">
                  <p className="text-lg mb-6">
                    At Estancia Corta, we respect your privacy and are committed to protecting your personal data. 
                    This privacy policy explains how we collect, use, and protect your information.
                  </p>

                  <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Information We Collect</h2>
                  <p className="mb-4">We collect the following types of personal information:</p>
                  <ul className="list-disc pl-6 mb-6">
                    <li><strong>Full name:</strong> To identify you during the booking process and communication</li>
                    <li><strong>Email address:</strong> To send you booking confirmations, updates, and important communications</li>
                    <li><strong>Phone number:</strong> To contact you directly about your booking and provide support during your stay</li>
                  </ul>

                  <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">How We Use Your Information</h2>
                  <p className="mb-4">We use your personal data to:</p>
                  <ul className="list-disc pl-6 mb-6">
                    <li>Process and confirm your bookings</li>
                    <li>Communicate with you about your stay</li>
                    <li>Provide customer support and service</li>
                    <li>Send you relevant information about our services</li>
                    <li>Improve our services and user experience</li>
                  </ul>

                  <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Data Protection</h2>
                  <p className="mb-6">
                    We implement appropriate technical and organizational security measures to protect your 
                    personal data against unauthorized access, alteration, disclosure, or destruction.
                  </p>

                  <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Information Sharing</h2>
                  <p className="mb-6">
                    We do not sell, trade, or transfer your personal information to third parties without your consent, 
                    except when necessary to provide our services or when required by law.
                  </p>

                  <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Your Rights</h2>
                  <p className="mb-4">You have the right to:</p>
                  <ul className="list-disc pl-6 mb-6">
                    <li>Access your personal data</li>
                    <li>Rectify incorrect information</li>
                    <li>Request deletion of your data</li>
                    <li>Withdraw your consent at any time</li>
                  </ul>

                  <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Contact</h2>
                  <p className="mb-6">
                    If you have questions about this privacy policy or wish to exercise your rights, 
                    you can contact us at:
                  </p>
                  <ul className="list-none mb-6">
                    <li><strong>Email:</strong> pedro@estanciacorta.com</li>
                    <li><strong>Phone:</strong> +52 3340860642</li>
                  </ul>

                  <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Policy Updates</h2>
                  <p className="mb-6">
                    We reserve the right to update this privacy policy at any time. 
                    We will notify you of significant changes via the registered email address.
                  </p>

                  <p className="text-sm text-gray-600 mt-8">
                    <strong>Last updated:</strong> January 2025
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PrivacyPolicy;